import { Injectable, inject } from '@angular/core';
import { DatabaseService } from '../database.service';

/**
 * Service responsible for preloading media (e.g. student profile images) 
 * so the Angular Service Worker caches them for offline use.
 */
@Injectable({
  providedIn: 'root'
})
export class MediaSyncService {
  private dbService = inject(DatabaseService);

  /**
   * Scans local Dexie database for students and actively fetches their
   * profile_image URLs. This allows the Service Worker 'dataGroups' rule 
   * to intercept the network request and cache the image natively.
   */
  async preloadStudentImages(): Promise<void> {
    try {
      const db = await this.dbService.ready();
      const students = await db.students.toArray();
      
      const imageUrls = new Set<string>();
      
      // Collect valid image URLs
      for (const student of students) {
        if (student.profile_image && student.profile_image.startsWith('http')) {
          imageUrls.add(student.profile_image);
        }
      }

      console.info(`[MediaSync] Preloading ${imageUrls.size} student profile images for offline caching...`);

      // We process them in chunks so we don't bombard the network
      const urlsArray = Array.from(imageUrls);
      const chunkSize = 10;
      
      for (let i = 0; i < urlsArray.length; i += chunkSize) {
        const chunk = urlsArray.slice(i, i + chunkSize);
        
        // We fetch normally. If the CDN lacks CORS headers, this will fail.
        // DO NOT use 'no-cors' as it creates 'opaque responses' which consume
        // ~7MB of cache quota per image and crash the Service Worker!
        await Promise.allSettled(
          chunk.map(url => fetch(url, { cache: 'force-cache' }))
        );
      }

      console.info('[MediaSync] Profile image preloading complete.');
    } catch (e) {
      console.error('[MediaSync] Failed to preload media:', e);
    }
  }

  hydrationTask() {
    return {
      key: 'media',
      labelKey: 'offline.loadingMedia',
      label: 'Loading student images for offline use',
      run: async (report: (records: number) => void) => {
        await this.preloadStudentImages();
        // Since we don't have an exact record count of images fetched instantly,
        // we can just report 1 completion or the number of students.
        const db = await this.dbService.ready();
        const students = await db.students.count();
        report(students);
        return students;
      }
    };
  }
}
